import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Dataset } from '@/shared/api/generated/catalog';

/* Whether a dataset is a table, a view or a materialised view — as its source's catalogue reported
 * it at harvest, and never as its name suggests.
 *
 * **The kind is the server's, and this component only draws it.** It takes `object_type` and nothing
 * else: no name, no schema, so there is no way to write `name.endsWith('_view')` here or in a caller
 * that wanted the icon without the field. A table called `accounts_view` and a view called
 * `customer_summary` are both ordinary, and a suffix test gets both wrong.
 *
 * **The two glyphs are the supplied artwork, unredrawn.** `icons/dataset-view.png` and
 * `icons/dataset-table.png` are the supplied images' pixels with the button fill keyed out to alpha
 * — composited back over that fill they reproduce the source with zero difference — and they are
 * drawn through `mask-image` so the shape is exact while the colour follows the theme. Painting the
 * PNG directly would have kept a mid-grey glyph on the dark theme's dark surface.
 *
 * **`unknown` is drawn, and drawn as an absence.** A dataset harvested before the kind was recorded
 * carries it until its next harvest. Rendering nothing would let the column read as "these are
 * tables", and rendering the table glyph would say so outright; a dashed outline says "not known".
 *
 * The label is the accessible name (`role="img"`), and the same words appear as a tip on hover and
 * whenever the link placed right after it has keyboard focus — a native `title` shows on neither.
 * **Place it before the dataset's link, not inside it**, so the link's accessible name stays the
 * dataset's name rather than "Database Table retail_bank.accounts".
 */

export type DatasetObjectType = Dataset['object_type'];

export interface DatasetObjectTypeIconProps {
  /** The kind the API reported. `undefined` for a caller whose lookup did not reach this dataset,
   * which renders exactly as `unknown` rather than guessing. */
  readonly objectType: DatasetObjectType | undefined;
}

export function DatasetObjectTypeIcon({
  objectType,
}: DatasetObjectTypeIconProps): ReactElement {
  const { t } = useTranslation();
  const kind = objectType ?? 'unknown';
  const label = t(`objectType.${kind}`);
  return (
    <span
      className="lq-otype"
      data-kind={kind}
      role="img"
      aria-label={label}
      data-tip={label}
    >
      <span className="lq-otype__glyph" aria-hidden="true" />
    </span>
  );
}
